import React, { useState, useMemo, useEffect, useRef } from 'react';
import Game from './Game'
import HostLobby from './HostLobby'
import HostViewport from './HostViewport'
import showDirectory from '../data/jeopardy_questions_directory'
import roomCodeFilter from '../data/room_code_filter.json'
import '../css/HostClient.css';
import { v4 as uuid } from 'uuid';
import {EVENT_TYPES, sendEvent} from '../GameEvent'

import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import "firebase/compat/analytics";

import {ROOM_CODE_LENGTH} from '../constants.js'

const blockedRoomCodes = new Set(roomCodeFilter)

function generateCode(length=ROOM_CODE_LENGTH) {
  // 65-90 inclusive for capital letters
  let code
  do {
    code = ''
    for (let i=0;i<length;i++) {
      let charCode = ~~(Math.random()*26)+65
      code += String.fromCharCode(charCode)
    }
  } while (blockedRoomCodes.has(code.toLowerCase()))
  return code
}

const startVolume = localStorage.getItem('volume') ?? 100

function HostClient() {
  let [optFullGame, setOptFullGame] = useState(true)
  let [optNoMedia, setOptNoMedia] = useState(true)
  let shows = useMemo(()=>showDirectory.filter(v=>{
    let fullGame = optFullGame ? v.full_game : true
    let noMedia = optNoMedia ? !v.has_media : true
    return fullGame && noMedia
  }),[optFullGame, optNoMedia])
  let [roomCode, setRoomCode] = useState(()=>{let id = generateCode(); localStorage.setItem('roomCode', id); return id})
  let [gameId, setGameId] = useState(uuid)
  let [show, setShow] = useState({ id: null, airDate: null })
  let [selectedDate, setSelectedDate] = useState(()=>shows[Math.floor(Math.random() * shows.length)]?.date ?? '')
  let [isLoadingShow, setIsLoadingShow] = useState(true)
  let [showError, setShowError] = useState('')
  let [loadAttempt, setLoadAttempt] = useState(0)
  const showRequest = useRef(0)
  let [isDashboardVisible, setIsDashboardVisible] = useState(false)
  let [players, setPlayers] = useState([])
  let [isMuted, setIsMuted] = useState(false)
  let [volume, setVolume] = useState(startVolume)
  useEffect(()=>{
    const controller = new AbortController()
    const request = ++showRequest.current
    setIsLoadingShow(true)
    setShowError('')
    async function loadShow() {
      try {
        if (!selectedDate) throw new Error('No games match the selected filters.')
        const response = await fetch(`${process.env.PUBLIC_URL}/games/jeopardy_${selectedDate}.json`, { signal: controller.signal })
        if (!response.ok) throw new Error(`Unable to load game (${response.status}).`)
        const json = await response.json()
        if (json.id == null || !json.airDate || !json.rounds) throw new Error('Invalid game file.')
        if (request === showRequest.current && !controller.signal.aborted) changeShow(json, selectedDate)
      } catch (error) {
        if (request === showRequest.current && !controller.signal.aborted) setShowError(error.message)
      } finally {
        if (request === showRequest.current && !controller.signal.aborted) setIsLoadingShow(false)
      }
    }
    loadShow()
    return ()=>controller.abort()
    // Load only when a selection or retry changes, not when game state updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, loadAttempt])
  let [audio] = useState(()=>{
    let _audio = {
      intro: new Audio('/audio/intro.mp3'),
      finalJeopardy: new Audio('/audio/finalJeopardy.mp3'),
      dailyDouble: new Audio('/audio/dailyDouble.mp3'),
      boardFill: new Audio('/audio/boardFill.mp3'),
      timer: new Audio('/audio/timer.mp3'),
    }

    Object.values(_audio).forEach(v=>{
      v.volume = startVolume/100
    })
    _audio.intro.volume *= .4
    _audio.dailyDouble.volume *= .25

    return _audio
  })

  // init
  function initGameModel(_roomCode=roomCode, _gameId=gameId) {
    console.debug('init')

    // TODO: should check if there is a lobby open with the game code already before setting it
    // 26^4 = 456,976 possible lobbies open at once

    // create firebase game model
    firebase.firestore().collection('games').doc(_gameId).set({
      roomCode: _roomCode,
      id: gameId,
      showId: show.id,
      airDate: show.airDate,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      startedAt: null,
      endedAt: null,
      // players
      // events
    }).catch((e)=>{
      console.error('Error creating firebase game model', e)
    })
  }
  useState(initGameModel)
  useState(()=>{
    firebase.analytics().setCurrentScreen('HostLobby')
  })

  function startGameHandler(_players, _gameId, _roomCode) {
    if (isLoadingShow || showError || show.id == null) return
    console.debug('startGameHandler:show',show)
    startGame(undefined, _players, _gameId, _roomCode)
  }

  // eslint-disable-next-line no-unused-vars
  function endGame() {
    firebase.analytics().logEvent('host__game_ended', {gameId, roomCode, showId: show.id})
    sendEvent(EVENT_TYPES.GAME_ENDED, show.id, gameId)

    // init new lobby
    let _roomCode = generateCode()
    let _gameId = uuid()
    localStorage.setItem('roomCode', _roomCode) // for ease of development

    initGameModel(_roomCode, _gameId)

    setRoomCode(_roomCode)
    setGameId(_gameId)
    setPlayers([])
  }

  function startGame(_show = show, _players = players, _gameId = gameId, _roomCode = roomCode) {
    console.debug('startGame:show',_show)
    firebase.analytics().logEvent('host__game_started', {gameId: _gameId, roomCode: _roomCode, showId: _show.id})
    sendEvent(EVENT_TYPES.GAME_STARTED, _show.id, _gameId)

    // kill any lingering sound from previous game
    Object.values(audio).forEach(v=>v.pause())

    setPlayers(_players)
    firebase.analytics().setCurrentScreen('Game')
  }

  function toggleDashboard() {
    firebase.analytics().logEvent('host__toggle_dashboard_click', {gameId, roomCode, isShowing: !isDashboardVisible})
    setIsDashboardVisible(!isDashboardVisible)
  }

  // selects a random show if no airDate given
  function selectShow(airDate) {
    console.debug('host selected show')
    if (!airDate) {
      let randomShow = ~~(Math.random() * shows.length)
      airDate = shows[randomShow]?.date ?? ''
    }
    firebase.analytics().logEvent('host__selected_show', {gameId, roomCode, airDate, uploaded: false})

    setIsLoadingShow(true)
    setSelectedDate(airDate)
    setLoadAttempt(value=>value+1)
  }

  function uploadShow(file) {
    if (!file) return
    console.debug('host uploaded show')
    firebase.analytics().logEvent('host__selected_show', {gameId, roomCode, airDate: null, uploaded: true})

    const request = ++showRequest.current
    setIsLoadingShow(true)
    setShowError('')
    file.text().then((data)=>{
      let json = JSON.parse(data)
      if (json.id == null || !json.rounds) throw new Error('Invalid game file.')
      if (request === showRequest.current) changeShow(json)
    }).catch(error=>{
      if (request === showRequest.current) setShowError(error.message)
    }).finally(()=>{
      if (request === showRequest.current) setIsLoadingShow(false)
    })
  }

  function changeShow(_show, airDate=null) {
    console.debug('host show - show loaded')
    firebase.analytics().logEvent('host__selected_show_loaded', {gameId, roomCode, showId: _show.id, airDate})

    // update firebase store with current show info
    firebase.firestore().collection('games').doc(gameId).update({
      showId: _show.id,
      airDate,
    }).catch((e)=>{
      console.error('Error updating firebase game model', e)
    })

    setShow(_show)
    if (players.length) startGame(_show)
  }

  function changeOptFullGame(value) {
    firebase.analytics().logEvent('host__changed_game_filter', {gameId, roomCode, settingsChanged: {optFullGame: value}})
    setOptFullGame(value)
  }

  function changeOptNoMedia(value) {
    firebase.analytics().logEvent('host__changed_game_filter', {gameId, roomCode, settingsChanged: {optNoMedia: value}})
    setOptNoMedia(value)
  }

  function updateMute(muted) {
    Object.values(audio).forEach(sound=>{ sound.muted = muted })
    setIsMuted(muted)
  }

  function toggleMute() {
    updateMute(!isMuted)
  }

  function updateVolume(value) {
    Object.values(audio).forEach(v=>{
      v.volume = value/100
    })
    audio.intro.volume *= .4
    audio.dailyDouble.volume *= .25
    setVolume(value)
    updateMute(false)
    localStorage.setItem('volume', value)
  }

  return (
    <>
      <HostViewport>
        { players.length === 0
          ? <HostLobby key={gameId} {...{gameId, roomCode, show, startGameHandler, isMuted, toggleMute}} canStart={!isLoadingShow && !showError && show.id != null} />
          : <Game {...{gameId, show, players, roomCode, audio, isMuted, toggleMute}} />}
      </HostViewport>
      <div className="host-controls controls">
        {isLoadingShow && <div role="status">Loading game…</div>}
        {showError && <div role="alert">{showError} <button onClick={()=>setLoadAttempt(value=>value+1)}>Retry</button></div>}
        { isDashboardVisible ? (
          <div className="dashboard">
            <div>
              <button onClick={()=>{selectShow()}}>Load Random Show</button>
            </div>
            <div>
              <select className="game-select" value={isLoadingShow ? selectedDate : show.airDate ?? ''} onChange={(e)=>{selectShow(e.target.value)}}>
                <option value="" disabled>Select a game</option>
                {shows.map(v=>(<option value={v.date} key={v.date}>{v.date} ({v.show_id})</option>))}
              </select>
              <input type="checkbox" id="fullGame" name="fullGame" checked={optFullGame} onChange={(e)=>changeOptFullGame(e.target.checked)} />
              <label htmlFor="fullGame">Full Game</label>
              <input type="checkbox" id="noMedia" name="noMedia" checked={optNoMedia} onChange={(e)=>changeOptNoMedia(e.target.checked)} />
              <label htmlFor="noMedia">No Media</label>
            </div>
            <div>
              <input type="file" onChange={(e)=>{uploadShow(e.target.files[0])}} />
            </div>
            <div>
              <label htmlFor="volume">Volume:</label>
              <input type="range" id="volume" name="volume" min="0" max="100" value={volume} step="5" onChange={(e)=>updateVolume(e.target.value)}/>
            </div>
          </div>)
        : null }
        <button onClick={toggleDashboard}>Toggle Dashboard</button>
      </div>
    </>
  )
}

export default HostClient;
